/**
 * Language Detection Service
 *
 * Detects user's preferred language from browser settings.
 * Maps browser language codes to supported app languages (de, fr, en).
 * Used on first app load before any UI renders.
 *
 * Mapping rules:
 * - de-* (de-CH, de-DE, de-AT) → 'de'
 * - fr-* (fr-CH, fr-FR, fr-CA) → 'fr'
 * - en-* (en-US, en-GB, etc.) → 'en'
 * - Everything else → 'de' (German default for Swiss context)
 */

export type SupportedLanguage = 'de' | 'fr' | 'en';

/**
 * The authoritative list of supported app languages. Single source so callers
 * (language picker, stored-value guard, profile editor) never re-hardcode the
 * triple; adding a language here surfaces it everywhere at once.
 */
export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = ['de', 'fr', 'en'] as const;

/** Type guard: is the value one of the supported languages? */
export function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

const STORAGE_KEY = 'icu_wissen_lang';

/**
 * Maps a browser language code to a supported app language.
 *
 * @param browserLang - Browser language code (e.g., 'de-CH', 'fr', 'en-US')
 * @returns Mapped app language ('de', 'fr', or 'en')
 */
export function mapBrowserLanguage(browserLang: string | undefined): SupportedLanguage {
  if (!browserLang) {
    return 'de';
  }

  const normalized = browserLang.toLowerCase().trim();

  // Check for English variants (en, en-US, en-GB, etc.)
  if (normalized.startsWith('en')) {
    return 'en';
  }

  // Check for French variants (fr, fr-CH, fr-FR, fr-CA, etc.)
  if (normalized.startsWith('fr')) {
    return 'fr';
  }

  // Check for German variants (de, de-CH, de-DE, de-AT, etc.)
  if (normalized.startsWith('de')) {
    return 'de';
  }

  // Everything else defaults to German (Swiss context)
  return 'de';
}

/**
 * Detects the user's preferred language from browser settings.
 * Checks navigator.languages first (ordered preference list),
 * then falls back to navigator.language.
 *
 * @returns Detected language ('de', 'fr', or 'en')
 */
export function detectBrowserLanguage(): SupportedLanguage {
  // Check navigator.languages (array of preferred languages)
  if (typeof navigator !== 'undefined') {
    // navigator.languages is an ordered array of user's preferred languages
    const languages = navigator.languages;
    if (languages && languages.length > 0) {
      // Check first preference only (most preferred language)
      return mapBrowserLanguage(languages[0]);
    }

    // Fallback to navigator.language (single value)
    if (navigator.language) {
      return mapBrowserLanguage(navigator.language);
    }
  }

  // Ultimate fallback: German
  return 'de';
}

/**
 * Gets the stored language preference from localStorage.
 *
 * @returns Stored language or null if not set
 */
export function getStoredLanguage(): SupportedLanguage | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isSupportedLanguage(stored) ? stored : null;
  } catch {
    // localStorage might be unavailable (private mode, etc.)
    return null;
  }
}

/**
 * Stores the language preference in localStorage.
 *
 * @param language - Language to store
 */
export function storeLanguage(language: SupportedLanguage): void {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Silently fail if localStorage unavailable
  }
}

/**
 * Gets the initial language for the app.
 * Priority:
 * 1. Stored preference (returning user)
 * 2. Browser detection (new user)
 *
 * @returns Language to use
 */
export function getInitialLanguage(): SupportedLanguage {
  // First check if user has a stored preference
  const stored = getStoredLanguage();
  if (stored) {
    return stored;
  }

  // Detect from browser for new users
  return detectBrowserLanguage();
}

/**
 * Checks if this is a new user (no stored language preference).
 * Used to determine if we should store the detected language.
 *
 * @returns true if no stored preference exists
 */
export function isNewUser(): boolean {
  return getStoredLanguage() === null;
}

/**
 * resolveExplicitDetectionLanguage decides whether the running app's active
 * language should be corrected to `configDefault`, for a deployment whose
 * `config.language.detection` is `"explicit"` (config/app.yaml — the
 * institution wants every new visitor to start on the deployment's own
 * default, never a browser-sniffed guess).
 *
 * i18n itself initializes at MODULE-IMPORT time (i18n/index.ts), before
 * GET /config has even been requested — so this correction runs LATER, once
 * config has loaded (App.tsx), as a one-time override of whatever
 * `detectBrowserLanguage()` picked at boot. It only ever overrides that
 * one-time guess: a real choice — a logged-in user's own profile language,
 * or a language the visitor (or a previous pass of this same correction)
 * already stored — always wins and is left untouched.
 *
 * Pure and side-effect-free (no localStorage/navigator/i18n reads) so it's
 * testable without mocking any of those — the caller supplies every input
 * it needs and applies the returned language itself.
 *
 * @returns the language to switch to, or null when no correction applies.
 */
export function resolveExplicitDetectionLanguage(params: {
  /** config.language.detection ("browser" | "explicit" on the wire; a plain
   *  string here since the frontend type doesn't narrow it — see api/types.ts). */
  detection: string;
  /** config.language.default. */
  configDefault: string;
  /** The logged-in user's own profile language, when known. */
  userLanguage: string | null | undefined;
  /** True when a stored preference already exists (getStoredLanguage() !== null) —
   *  a real prior choice, distinct from "nobody has chosen yet". */
  hasStoredPreference: boolean;
  /** i18n's currently active language. */
  currentLanguage: string;
}): SupportedLanguage | null {
  if (params.detection !== 'explicit') return null;
  if (params.userLanguage) return null;
  if (params.hasStoredPreference) return null;
  if (!isSupportedLanguage(params.configDefault)) return null;
  if (params.configDefault === params.currentLanguage) return null;
  return params.configDefault;
}
